/**
 * Activity Tracking Service
 * 
 * Tracks user activity for AI-driven personalization and follow-up emails.
 * Aggregates events per day to minimize Firestore writes and provide
 * AI-friendly context for personalized communication.
 * 
 * Storage: masterProfiles/{uid}/activity/{YYYY-MM-DD}
 * 
 * Key features:
 * - Daily aggregation (merge events per day)
 * - Rolling window (last 30 days kept)
 * - AI-friendly summaries for email personalization
 * - Lightweight: batches writes, minimal reads
 * 
 * @author Heseda AI Team
 * @version 1.0
 */

import { doc, getDoc, setDoc, updateDoc, collection, getDocs, query, orderBy, limit, deleteDoc, runTransaction } from 'firebase/firestore';
import { getCurrentUserId } from './apiAuth';
import { db } from './firebaseConfig';
import { auth } from './firebaseConfig';
import type { DailyActivity, UserInterests, ProductSource } from '../types/profile';

// ============================================================================
// Constants
// ============================================================================

/** Keep last N days of activity */
const ACTIVITY_RETENTION_DAYS = 30;

/** Debounce writes by this many milliseconds */
const WRITE_DEBOUNCE_MS = 5000;

/** In-memory buffer for current session */
let sessionBuffer: Partial<DailyActivity> = {};
let writeTimeout: ReturnType<typeof setTimeout> | null = null;
let currentUserId: string | null = null;

// ============================================================================
// Helpers
// ============================================================================

/**
 * Get today's date in YYYY-MM-DD format
 */
function getToday(): string {
  return new Date().toISOString().split('T')[0];
}

/**
 * Get Firestore document reference for a day's activity
 */
function getActivityDocRef(uid: string, date: string) {
  return doc(db, 'masterProfiles', uid, 'activity', date);
}

// ============================================================================
// Core Functions
// ============================================================================

/**
 * Initialize activity tracking for a user session
 */
export function initActivityTracking(uid: string): void {
  currentUserId = uid;
  sessionBuffer = {
    date: getToday(),
    pageViews: [],
    conversationTopics: [],
    productInteractions: [],
    searches: [],
    actions: [],
    sessions: 1,
    totalMessages: 0,
  };
  
  // Track session start
  trackAction('login', {});
}

/**
 * Immediately persist lastVisitDate to Firestore.
 * Call this AFTER any access-gating checks (e.g. pilot whitelist)
 * so that denied users don't get a visit recorded.
 *
 * Uses dot-notation with updateDoc to avoid overwriting other segment fields.
 * Falls back to setDoc for new profiles that don't exist yet.
 */
export function updateLastVisitDate(uid: string): Promise<void> {
  const profileRef = doc(db, 'masterProfiles', uid);
  const lastVisitDate = new Date().toISOString();

  return updateDoc(profileRef, { 'segment.lastVisitDate': lastVisitDate })
    .catch((err: unknown) => {
      const errorCode = (err as { code?: string }).code;

      // Only fall back to setDoc when the document truly does not exist.
      if (errorCode === 'not-found' || errorCode === 'NOT_FOUND') {
        return setDoc(profileRef, { segment: { lastVisitDate } }, { merge: true })
          .catch((fallbackErr: unknown) => {
            console.error('Failed to create/merge lastVisitDate via setDoc:', fallbackErr);
          });
      }

      // For all other errors, log and do not mask the original failure.
      console.error('Failed to update lastVisitDate via updateDoc:', err);
    });
}

/**
 * Track a page view
 */
export function trackPageView(
  page: 'home' | 'chat' | 'voice-chat' | 'profile' | 'cart' | 'checkout' | 'product' | 'editor-picks' | 'web-search' | 'wishlist' | 'settings'
): void {
  if (!sessionBuffer.pageViews) sessionBuffer.pageViews = [];
  
  const existing = sessionBuffer.pageViews.find(p => p.page === page);
  if (existing) {
    existing.visitCount++;
  } else {
    sessionBuffer.pageViews.push({ 
      id: `${page}:${Date.now()}:${Math.random().toString(36).slice(2,8)}`,
      page, 
      visitCount: 1 
    });
  }

  scheduleWrite();
}

/**
 * Track a conversation topic discussed
 */
export function trackConversationTopic(topic: string): void {
  if (!sessionBuffer.conversationTopics) sessionBuffer.conversationTopics = [];
  
  const normalizedTopic = topic.toLowerCase().trim();
  if (!sessionBuffer.conversationTopics.includes(normalizedTopic)) {
    sessionBuffer.conversationTopics.push(normalizedTopic);
  }
  
  scheduleWrite();
}

/**
 * Track a product interaction
 */
export function trackProductInteraction(
  productId: string,
  productName: string,
  source: ProductSource,
  action: 'view' | 'click' | 'add-to-cart' | 'remove-from-cart'
): void {
  if (!sessionBuffer.productInteractions) sessionBuffer.productInteractions = [];
  
  const existing = sessionBuffer.productInteractions.find(p => p.productId === productId);
  if (existing) {
    if (!existing.actions.includes(action)) {
      existing.actions.push(action);
    }
  } else {
    sessionBuffer.productInteractions.push({
      productId,
      productName,
      source ,
      actions: [action],
    });
  }
  
  scheduleWrite();
}

/**
 * Track a search query
 */
export function trackSearch(keyword: string): void {
  if (!sessionBuffer.searches) sessionBuffer.searches = [];
  
  const normalizedKeyword = keyword.toLowerCase().trim();
  if (!sessionBuffer.searches.includes(normalizedKeyword)) {
    sessionBuffer.searches.push(normalizedKeyword);
  }
  
  scheduleWrite();
}

/**
 * Track a key action
 */
export function trackAction(
  type: 'login' | 'signup' | 'profile-update' | 'sample-request' | 
        'address-added' | 'checkout-started' | 'purchase' | 'feedback-submitted',
  details: Record<string, unknown> = {}
): void {
  if (!sessionBuffer.actions) sessionBuffer.actions = [];

  // Add a stable-ish id to help deduplication on merge/transaction
  const timestamp = new Date().toISOString();
  const id = `${type}:${Date.now()}:${Math.random().toString(36).slice(2,8)}`;

  sessionBuffer.actions.push({
    id,
    type,
    timestamp,
    details,
  });
  
  scheduleWrite();
}

/**
 * Track new chat messages (count only — full text is persisted by chatHistoryService).
 *
 * @param count - number of NEW messages in this exchange (e.g. 2 for a user+bot pair)
 */
export function trackMessageCount(count: number): void {
  if (!sessionBuffer.totalMessages) sessionBuffer.totalMessages = 0;
  sessionBuffer.totalMessages += count;
  scheduleWrite();
}

// ============================================================================
// Write Logic (Debounced)
// ============================================================================

/**
 * Schedule a write to Firestore (debounced)
 */
function scheduleWrite(): void {
  if (writeTimeout) {
    clearTimeout(writeTimeout);
  }
  
  writeTimeout = setTimeout(() => {
    flushToFirestore();
  }, WRITE_DEBOUNCE_MS);
}

/**
 * Flush buffered activity to Firestore
 */
async function flushToFirestore(): Promise<void> {
  // Guard: skip if user has signed out — Firestore rules reject unauthenticated writes
  if (!auth.currentUser) {
    sessionBuffer = {};
    currentUserId = null;
    if (writeTimeout) {
      clearTimeout(writeTimeout);
      writeTimeout = null;
    }
    return;
  }

  // If currentUserId not set, attempt a synchronous fallback to auth helper
  if (!currentUserId) {
    const fallback = getCurrentUserId();
    if (fallback) {
      currentUserId = fallback;
    }
  }

  if(!sessionBuffer.date) {
    sessionBuffer.date = new Date().toISOString().split('T')[0];
  }
  if (!currentUserId || !sessionBuffer.date) return;
  
  try {
    const docRef = getActivityDocRef(currentUserId, sessionBuffer.date);
    // Use transaction to avoid race conditions and duplicates
    await runTransaction(db, async (tx) => {
      const snap = await tx.get(docRef);
      if (!snap.exists()) {
        tx.set(docRef, sessionBuffer);
        return;
      }

      const existing = snap.data() as DailyActivity;

      // Merge deterministically with dedup for actions/messages
      const incoming = sessionBuffer as DailyActivity;

      const merged: DailyActivity = mergeActivity(existing, incoming);

      // Deduplicate actions by id if present, otherwise by type:timestamp
      const actionMap = new Map<string, DailyActivity['actions'][number]>();
      for (const a of merged.actions || []) {
        const key = a.id || `${a.type}:${a.timestamp}`;
        if (!actionMap.has(key)) {
          actionMap.set(key, a);
        }
      }
      merged.actions = Array.from(actionMap.values());

      tx.set(docRef, merged, { merge: true });
    });

    // Update lastVisitDate on the parent masterProfile for admin dashboard visibility
    // Use setDoc with merge so it works even if the profile doc doesn't exist yet
    const profileRef = doc(db, 'masterProfiles', currentUserId);
    await setDoc(profileRef, { segment: { lastVisitDate: new Date().toISOString() } }, { merge: true });
  } catch (error) {
    console.error('❌ Failed to flush activity:', error);
  } finally {
    sessionBuffer = {};
  }
}

/**
 * Merge two activity records (existing + new)
 */
function mergeActivity(existing: DailyActivity, incoming: DailyActivity): DailyActivity {
  return {
    date: existing.date,
    
    // Merge page views
    pageViews: mergePageViews(existing.pageViews || [], incoming.pageViews || []),
    
    // Merge topics (unique)
    conversationTopics: [...new Set([
      ...(existing.conversationTopics || []),
      ...(incoming.conversationTopics || []),
    ])],
    
    // Merge product interactions
    productInteractions: mergeProductInteractions(
      existing.productInteractions || [],
      incoming.productInteractions || []
    ),
    
    // Merge searches (unique)
    searches: [...new Set([
      ...(existing.searches || []),
      ...(incoming.searches || []),
    ])],
    
    // Append actions
    actions: [
      ...(existing.actions || []),
      ...(incoming.actions || []),
    ],
    
    // Sum sessions and message counts (full text lives in masterProfiles/{uid}/chatMessages)
    sessions: (existing.sessions || 0) + (incoming.sessions || 0),
    totalMessages: (existing.totalMessages || 0) + (incoming.totalMessages || 0),
  };
}

function mergePageViews(
  existing: DailyActivity['pageViews'],
  incoming: DailyActivity['pageViews']
): DailyActivity['pageViews'] {
  const merged: DailyActivity['pageViews'] = [];
  for (const view of incoming) {
    const found = existing.find(m => m.page === view.page);
    if (found) {
      // Create new object instead of mutating existing
      const mergedView: DailyActivity['pageViews'][number] = {
        ...found,
        visitCount: found.visitCount + view.visitCount
      };
      if (view.totalTimeSeconds) {
        mergedView.totalTimeSeconds = (found.totalTimeSeconds || 0) + view.totalTimeSeconds;
      }
      merged.push(mergedView);
    } else {
      merged.push(view);
    }
  }

  existing.forEach(ev => {
    const found = merged.find(m => m.page === ev.page);
    if (!found) {
      merged.push(ev);
    }
  });

  return merged;
}

function mergeProductInteractions(
  existing: DailyActivity['productInteractions'],
  incoming: DailyActivity['productInteractions']
): DailyActivity['productInteractions'] {
  const merged = [...existing];
  
  for (const interaction of incoming) {
    const found = merged.find(m => m.productId === interaction.productId);
    if (found) {
      found.actions = [...new Set([...found.actions, ...interaction.actions])];
    } else {
      merged.push(interaction);
    }
  }
  
  return merged;
}

// ============================================================================
// Read Functions (For AI Context)
// ============================================================================

/**
 * Get recent activity for AI context
 * @param uid User ID
 * @param days Number of days to retrieve (default 7)
 * @returns Array of daily activity summaries
 */
export async function getRecentActivity(uid: string, days = 7): Promise<DailyActivity[]> {
  try {
    const activityRef = collection(db, 'masterProfiles', uid, 'activity');
    const q = query(activityRef, orderBy('date', 'desc'), limit(days));
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(doc => doc.data() as DailyActivity);
  } catch (error) {
    console.error('❌ Failed to get recent activity:', error);
    return [];
  }
}

/**
 * Compute user interests from recent activity
 * @param activity Recent activity data
 * @returns Aggregated user interests
 */
export function computeUserInterests(activity: DailyActivity[]): UserInterests {
  const concerns = new Map<string, number>();
  const categories = new Map<string, number>();
  const brands = new Map<string, number>();
  
  // Count occurrences
  for (const day of activity) {
    // From conversation topics
    for (const topic of day.conversationTopics || []) {
      concerns.set(topic, (concerns.get(topic) || 0) + 1);
    }
    
    // From product interactions
    for (const interaction of day.productInteractions || []) {
      const actionWeight = interaction.actions.includes('add-to-cart') ? 3 :
                          interaction.actions.includes('click') ? 2 : 1;
      // Could extract category/brand from productName if we had catalog data
    }
    
    // From searches
    for (const search of day.searches || []) {
      concerns.set(search, (concerns.get(search) || 0) + 1);
    }
  }
  
  // Sort and get top items
  const topConcerns = [...concerns.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5)
    .map(([concern]) => concern);
  
  // Determine engagement score based on recency and frequency
  const totalSessions = activity.reduce((sum, day) => sum + (day.sessions || 0), 0);
  const totalMessages = activity.reduce((sum, day) => sum + (day.totalMessages || 0), 0);
  const daysSinceLastActivity = activity.length > 0 
    ? Math.floor((Date.now() - new Date(activity[0].date).getTime()) / (1000 * 60 * 60 * 24))
    : 999;
  
  let engagementScore: 'cold' | 'warm' | 'hot' = 'cold';
  if (daysSinceLastActivity <= 3 && totalMessages >= 5) {
    engagementScore = 'hot';
  } else if (daysSinceLastActivity <= 7 && totalMessages >= 2) {
    engagementScore = 'warm';
  }
  
  return {
    topConcerns,
    priceRange: { min: 0, max: 100 },  // Default, could be computed from product interactions
    categories: [...categories.keys()].slice(0, 5),
    interestedBrands: [...brands.keys()].slice(0, 5),
    engagementScore,
    updatedAt: new Date().toISOString(),
  };
}

/**
 * Get AI-friendly summary of user activity for email personalization
 */
export async function getActivitySummaryForAI(uid: string): Promise<string> {
  const activity = await getRecentActivity(uid, 7);
  
  if (activity.length === 0) {
    return 'No recent activity recorded.';
  }
  
  const interests = computeUserInterests(activity);
  const totalMessages = activity.reduce((sum, day) => sum + (day.totalMessages || 0), 0);
  const allTopics = [...new Set(activity.flatMap(day => day.conversationTopics || []))];
  const allSearches = [...new Set(activity.flatMap(day => day.searches || []))];
  const productsViewed = activity.flatMap(day => day.productInteractions || []).map(p => p.productName);
  
  const parts: string[] = [];
  
  if (interests.engagementScore === 'hot') {
    parts.push('User is highly engaged (visited recently, sent multiple messages).');
  } else if (interests.engagementScore === 'warm') {
    parts.push('User has moderate engagement.');
  } else {
    parts.push('User has been inactive for a while.');
  }
  
  if (allTopics.length > 0) {
    parts.push(`Discussed topics: ${allTopics.slice(0, 5).join(', ')}.`);
  }
  
  if (allSearches.length > 0) {
    parts.push(`Searched for: ${allSearches.slice(0, 5).join(', ')}.`);
  }
  
  if (productsViewed.length > 0) {
    parts.push(`Viewed products: ${productsViewed.slice(0, 5).join(', ')}.`);
  }
  
  return parts.join(' ');
}

// ============================================================================
// Cleanup Functions
// ============================================================================

/**
 * Clean up old activity data (keep last N days)
 */
export async function cleanupOldActivity(uid: string): Promise<void> {
  try {
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - ACTIVITY_RETENTION_DAYS);
    const cutoffStr = cutoffDate.toISOString().split('T')[0];
    
    const activityRef = collection(db, 'masterProfiles', uid, 'activity');
    const snapshot = await getDocs(activityRef);
    
    const deletePromises = snapshot.docs
      .filter(doc => doc.id < cutoffStr)
      .map(doc => deleteDoc(doc.ref));
    
    await Promise.all(deletePromises);
  } catch (error) {
    console.error('❌ Failed to cleanup old activity:', error);
  }
}

/**
 * Flush activity on page unload
 */
export function setupUnloadHandler(): void {
  if (typeof window !== 'undefined') {
    window.addEventListener('beforeunload', () => {
      if (writeTimeout) {
        clearTimeout(writeTimeout);
      }
      // Use sendBeacon for reliable unload writes
      // For now, just flush synchronously (not ideal but works)
      flushToFirestore();
    });
  }
}

// ============================================================================
// Exports
// ============================================================================

export default {
  initActivityTracking,
  trackPageView,
  trackConversationTopic,
  trackProductInteraction,
  trackSearch,
  trackAction,
  trackMessageCount,
  getRecentActivity,
  computeUserInterests,
  getActivitySummaryForAI,
  cleanupOldActivity,
  setupUnloadHandler,
};
